'use client';

import { useContext, useEffect, useRef, useState } from 'react';

import { makeDebug } from '@ps/functions/utils/debug';

import {
  ChatGatewayContext,
  type ChatGatewayContextValue,
  type ChatGatewayEventListener,
  type GatewayConnectionStatus,
} from '../contexts/gateway/context';
import { isChatDebugEnabled } from '../functions/debug';
import { decodeChatGatewayEventData } from '../functions/decodeChatGatewayEventData';
import type { SocketEvent } from '../types/SocketEvent';

const debug = makeDebug('chat:gateway', isChatDebugEnabled('gateway'));

export function useChatGatewayContext(): ChatGatewayContextValue {
  const ctx = useContext(ChatGatewayContext);
  if (ctx == null) {
    throw new Error(
      'useChatGatewayContext must be used inside <ChatGatewayProvider>',
    );
  }
  return ctx;
}

/**
 * Subscribe a typed listener to a gateway event for the lifetime of
 * the calling component. The raw payload is decoded via
 * `decodeChatGatewayEventData` before the listener is invoked.
 */
export function useChatGatewayEvent<T extends SocketEvent>(
  event: T,
  listener: ChatGatewayEventListener<T>,
  tag: string,
) {
  const { addEventListener } = useChatGatewayContext();
  const listenerRef = useRef(listener);
  listenerRef.current = listener;

  useEffect(() => {
    const handler = (data: unknown) => {
      try {
        const decoded = decodeChatGatewayEventData(event, data) as Parameters<
          ChatGatewayEventListener<T>
        >[0];
        debug.info('received event', tag, event, decoded);
        listenerRef.current(decoded);
      } catch (e) {
        debug.error('error decoding event data', tag, data, e);
      }
    };
    return addEventListener(event, handler, tag);
  }, [addEventListener, event, tag]);
}

export function useChatGatewayConnectionStatus(): GatewayConnectionStatus {
  const { status$ } = useChatGatewayContext();
  const [value, setValue] = useState(status$.value);
  useEffect(() => {
    const sub = status$.subscribe(setValue);
    return () => sub.unsubscribe();
  }, [status$]);
  return value;
}
